import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  History as HistoryIcon,
  Visibility as ViewIcon,
  VerifiedUser as VerifiedIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { chickenRoadAPI } from '../../../services/api';

const ChickenRoadHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalSessions, setTotalSessions] = useState(0);
  const [statistics, setStatistics] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  
  const [filters, setFilters] = useState({
    status: '',
    difficulty: '',
    user_id: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    fetchHistory();
  }, [page, rowsPerPage]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      
      // Apply filters
      if (filters.status) params.status = filters.status;
      if (filters.difficulty) params.difficulty = filters.difficulty;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      // Use getAllSessions for admin view
      const response = await chickenRoadAPI.getAllSessions(params);
      if (response.data.success) {
        setSessions(response.data.sessions || []);
        setTotalSessions(response.data.total || 0);
        setStatistics(response.data.statistics || null);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (session) => {
    setSelectedSession(session);
    setDetailsDialogOpen(true);
  };

  const handleVerifySession = async (sessionId) => {
    try {
      const response = await chickenRoadAPI.verifySession(sessionId);
      if (response.data.success) {
        setVerificationData(response.data.verification);
        setVerificationDialogOpen(true);
        toast.success('Session verified successfully!');
      }
    } catch (err) {
      console.error('Error verifying session:', err);
      toast.error(err.response?.data?.message || 'Failed to verify session');
    }
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchHistory();
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      difficulty: '',
      user_id: '',
      date_from: '',
      date_to: '',
    });
    setPage(0);
    setTimeout(() => {
      fetchHistory();
    }, 100);
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'WON': return 'success';
      case 'LOST': return 'error';
      case 'ACTIVE': return 'warning';
      default: return 'default';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      case 'hardcore': return 'secondary';
      default: return 'default';
    }
  };

  const getMultiplierColor = (multiplier) => {
    const mult = parseFloat(multiplier);
    if (mult < 1.5) return 'default';
    if (mult < 3) return 'warning';
    if (mult < 5) return 'success';
    return 'primary';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 2 }} />
            🐔 Chicken Road History
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchHistory}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Statistics Cards */}
        {statistics && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Sessions</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {statistics.total_sessions || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Won: {statistics.games_won || 0} | Lost: {statistics.games_lost || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #f093fb20 0%, #f5576c20 100%)' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Wagered</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency(statistics.total_wagered)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #4facfe20 0%, #00f2fe20 100%)' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Total Paid</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {formatCurrency(statistics.total_paid)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: parseFloat(statistics.total_profit) >= 0 
                ? 'linear-gradient(135deg, #11998e20 0%, #38ef7d20 100%)'
                : 'linear-gradient(135deg, #eb575720 0%, #f4451820 100%)'
              }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>House Profit</Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: parseFloat(statistics.total_profit) >= 0 ? 'success.main' : 'error.main' 
                    }}
                  >
                    {formatCurrency(statistics.total_profit)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Win Rate: {statistics.win_rate || '0%'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="User ID"
                  type="number"
                  value={filters.user_id}
                  onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                  size="small"
                  placeholder="Filter by user"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="WON">Won</MenuItem>
                    <MenuItem value="LOST">Lost</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    value={filters.difficulty}
                    label="Difficulty"
                    onChange={(e) => setFilters({ ...filters, difficulty: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="easy">Easy</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="hard">Hard</MenuItem>
                    <MenuItem value="hardcore">Hardcore</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={handleApplyFilters}
                    fullWidth
                    size="small"
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    fullWidth
                    size="small"
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Sessions Table */}
        <Card>
          <CardContent>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Session #</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell align="center">Difficulty</TableCell>
                        <TableCell align="right">Bet Amount</TableCell>
                        <TableCell align="center">Final Stage</TableCell>
                        <TableCell align="center">Multiplier</TableCell>
                        <TableCell align="right">Payout</TableCell>
                        <TableCell align="right">House Profit</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Started At</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessions.map((session) => (
                        <TableRow key={session.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              #{session.session_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {session.username || `User${session.user_id}`}
                            </Typography>
                            {session.mobile && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {session.mobile}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={session.difficulty?.toUpperCase()}
                              color={getDifficultyColor(session.difficulty)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                              {formatCurrency(session.bet_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`Stage ${session.final_stage || 0}`}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${parseFloat(session.final_multiplier || 1).toFixed(2)}x`}
                              color={getMultiplierColor(session.final_multiplier)}
                              sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                              {formatCurrency(session.payout_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: parseFloat(session.house_profit) >= 0 ? 'success.main' : 'error.main',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end'
                              }}
                            >
                              {parseFloat(session.house_profit) >= 0 ? (
                                <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              ) : (
                                <TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              )}
                              {formatCurrency(session.house_profit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={session.status}
                              color={getStatusColor(session.status)}
                              size="small"
                              icon={session.status === 'WON' ? <TrophyIcon /> : undefined}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(session.started_at)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(session)}
                                color="primary"
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            {session.status !== 'ACTIVE' && (
                              <Tooltip title="Verify Fairness">
                                <IconButton
                                  size="small"
                                  onClick={() => handleVerifySession(session.id)}
                                  color="success"
                                >
                                  <VerifiedIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {sessions.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11} align="center">
                            <Typography color="text.secondary" sx={{ py: 3 }}>
                              No sessions found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalSessions}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Details Dialog */}
        <Dialog 
          open={detailsDialogOpen} 
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            🐔 Session #{selectedSession?.session_number} Details
          </DialogTitle>
          <DialogContent>
            {selectedSession && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Session Number:</Typography>
                  <Typography variant="h6" gutterBottom>#{selectedSession.session_number}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip
                    label={selectedSession.status}
                    color={getStatusColor(selectedSession.status)}
                    icon={selectedSession.status === 'WON' ? <TrophyIcon /> : undefined}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Difficulty:</Typography>
                  <Chip
                    label={selectedSession.difficulty?.toUpperCase()}
                    color={getDifficultyColor(selectedSession.difficulty)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Final Stage:</Typography>
                  <Typography variant="body1">Stage {selectedSession.final_stage || 0}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Bet Amount:</Typography>
                  <Typography variant="body1" color="primary.main">
                    {formatCurrency(selectedSession.bet_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Final Multiplier:</Typography>
                  <Chip
                    label={`${parseFloat(selectedSession.final_multiplier || 1).toFixed(2)}x`}
                    color={getMultiplierColor(selectedSession.final_multiplier)}
                    sx={{ fontWeight: 'bold' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Payout Amount:</Typography>
                  <Typography variant="body1" color="success.main">
                    {formatCurrency(selectedSession.payout_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Profit/Loss:</Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: parseFloat(selectedSession.profit_amount) >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 'bold'
                    }}
                  >
                    {formatCurrency(selectedSession.profit_amount)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Started At:</Typography>
                  <Typography variant="body1">{formatDateTime(selectedSession.started_at)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Ended At:</Typography>
                  <Typography variant="body1">{formatDateTime(selectedSession.ended_at)}</Typography>
                </Grid>

                {selectedSession.status !== 'ACTIVE' && selectedSession.server_seed && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" gutterBottom>Provably Fair Seeds:</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Server Seed (Revealed):</Typography>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {selectedSession.server_seed}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Server Seed Hash:</Typography>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {selectedSession.server_seed_hash}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
            {selectedSession && selectedSession.status !== 'ACTIVE' && (
              <Button 
                variant="contained" 
                startIcon={<VerifiedIcon />}
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleVerifySession(selectedSession.id);
                }}
              >
                Verify Fairness
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Verification Dialog */}
        <Dialog 
          open={verificationDialogOpen} 
          onClose={() => setVerificationDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              <VerifiedIcon sx={{ mr: 1, color: 'success.main' }} />
              Provably Fair Verification
            </Box>
          </DialogTitle>
          <DialogContent>
            {verificationData && (
              <Box>
                <Alert severity={verificationData.is_fair ? 'success' : 'error'} sx={{ mb: 2 }}>
                  {verificationData.is_fair 
                    ? '✅ Session is provably fair!'
                    : '❌ Verification failed!'
                  }
                </Alert>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Session Number:
                </Typography>
                <Typography variant="h6" gutterBottom>
                  #{verificationData.session_number}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Difficulty:
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {verificationData.difficulty?.toUpperCase()}
                </Typography>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Bomb Position (0-indexed):
                </Typography>
                <Typography variant="h5" gutterBottom sx={{ color: 'error.main', fontWeight: 'bold' }}>
                  {verificationData.bomb_position}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary">Server Seed Hash (Committed):</Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', display: 'block', mb: 2 }}>
                  {verificationData.server_seed_hash}
                </Typography>

                <Typography variant="body2" color="text.secondary">Server Seed (Revealed):</Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', display: 'block', mb: 2 }}>
                  {verificationData.server_seed}
                </Typography>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    The bomb position is determined using the server seed and client seed combination. 
                    You can independently verify this result using the provided seeds.
                  </Typography>
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVerificationDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
};

export default ChickenRoadHistory;
